import { extractBackendCode } from './extractBackendCode';

/**
 * THE INDEPENDENCE REFUSAL (#1215): `409 {message: 'Independence confirmation is required', code:
 * 'independenceRequired', auditId}`. Every audit route, and the team template routes that name an
 * audit, answer it to an auditor seated on the audit who has not confirmed independence
 * (DATA_FLOW.md §1). Nothing was read or written and the session goes on: the auditor confirms,
 * then the read goes again. Keyed to the code, never to a bare 409, which several writers read as
 * their own conflict.
 */
export const isIndependenceRefusal = (error: unknown): boolean =>
  (error as { response?: { status?: unknown } } | null)?.response?.status === 409 &&
  extractBackendCode(error) === 'independenceRequired';

/** The audit an independence refusal names, or `null` for any other error. */
export const independenceRefusalAuditId = (error: unknown): number | null => {
  if (!isIndependenceRefusal(error)) return null;
  const auditId = (error as { response: { data: { auditId?: unknown } } }).response.data.auditId;
  return typeof auditId === 'number' && Number.isInteger(auditId) && auditId > 0 ? auditId : null;
};

/** Answers whether it takes the refusal of `auditId`, that is, whether it asks the question. */
type IndependenceRefusalListener = (auditId: number) => boolean;

const listeners = new Set<IndependenceRefusalListener>();

/**
 * Lets the QueryClient's cache handlers, built at module scope outside the React tree, tell the
 * open dashboard that the server refused one of its reads or writes for want of independence, so
 * it asks again (`useIndependenceGate`). Returns the unsubscribe.
 */
export const onIndependenceRefusal = (listener: IndependenceRefusalListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Tells every listener, and answers whether one of them asks. Nobody asks off that audit's
 * dashboard: the caller then handles the refusal as any other error.
 */
export const emitIndependenceRefusal = (auditId: number): boolean => {
  let asked = false;
  listeners.forEach((listener) => {
    if (listener(auditId)) asked = true;
  });
  return asked;
};
